/**
 * Verification hooks — TanStack Query for identity verification flow.
 *
 * `useVerificationOverview` subscribes to realtime UPDATEs on the
 * user's `identity_documents` and `driver_licenses` rows so that
 * server-side state changes (verify-* edge fn outcomes, manual admin
 * review, future async webhook arrivals) flow through to the
 * verification overview screen without a manual refresh.
 */
import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../supabase'
import { verificationService } from '../services/verification.service'
import { useAuth, authKeys } from './use-auth'
import { identityKeys } from './use-identity'
import type {
  DriverLicenseInput,
  SelfieInput,
  LiveSelfieSession,
  LiveSelfieDecision,
} from '../types/verification.types'

// ─── Query Key Factory ─────────────────────────────────────────────────────

export const verificationKeys = {
  all: ['verification'] as const,
  overview: (userId: string) => [...verificationKeys.all, 'overview', userId] as const,
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

export function useVerificationOverview() {
  const { user } = useAuth()
  const userId = user?.id ?? ''
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: verificationKeys.overview(userId),
    queryFn: () => verificationService.getOverview(userId),
    enabled: !!userId,
    staleTime: 30 * 1000,
    retry: 2,
    refetchOnMount: true,
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: false,
  })

  // Realtime: invalidate the overview on any UPDATE to the user's
  // identity_documents or driver_licenses row. RLS scopes the
  // subscription to the row owner so we don't leak events across
  // accounts. Single channel, two listeners — fewer WebSocket
  // subscriptions than splitting into two channels.
  useEffect(() => {
    if (!userId) return
    const channel = supabase
      .channel(`verification:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'identity_documents',
          filter: `profile_id=eq.${userId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: verificationKeys.overview(userId) })
          // The identity reader listens to the same table and had no way to
          // hear about it. Without this, verifying a DUI left every screen that
          // asks "what identity has this person proven?" — the payout gate,
          // the AML prefill — answering with a five-minute-old "none". The
          // person completes verification and is still told to verify.
          void queryClient.invalidateQueries({ queryKey: identityKeys.all })
          // The profile badge (profiles.is_verified) is derived server-side by
          // this same row change — refresh it here so the badge, gates and host
          // chip converge immediately, not on the 10-minute profile staleTime.
          void queryClient.invalidateQueries({ queryKey: authKeys.profile(userId) })
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'driver_licenses',
          filter: `profile_id=eq.${userId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: verificationKeys.overview(userId) })
          void queryClient.invalidateQueries({ queryKey: authKeys.profile(userId) })
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'selfie_verifications',
          filter: `profile_id=eq.${userId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: verificationKeys.overview(userId) })
          void queryClient.invalidateQueries({ queryKey: authKeys.profile(userId) })
        },
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [userId, queryClient])

  return query
}

export function useSubmitLicense() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? ''

  return useMutation({
    mutationFn: (input: DriverLicenseInput) =>
      verificationService.submitDriverLicense(userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.overview(userId),
      })
    },
  })
}

export function useSubmitSelfie() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? ''

  return useMutation({
    mutationFn: (input: SelfieInput) => verificationService.submitSelfie(userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.overview(userId),
      })
    },
  })
}

export function useStartLiveSelfieSession() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? ''

  return useMutation<LiveSelfieSession, Error, { callbackUrl?: string; native?: boolean }>({
    mutationFn: (input) => verificationService.startLiveSelfieSession(userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.overview(userId),
      })
    },
  })
}

export function useRefreshLiveSelfieSession() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? ''

  return useMutation<LiveSelfieDecision, Error, { sessionId?: string; providerSessionId?: string }>(
    {
      mutationFn: (input) => verificationService.refreshLiveSelfieSession(userId, input),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: verificationKeys.overview(userId),
        })
      },
    },
  )
}
